import { useId, useMemo, useState } from 'react'

import Button from '@/components/ui/Button'
import ChipTabs from '@/components/ui/ChipTabs'
import Field from '@/components/ui/Field'
import Modal from '@/components/ui/Modal'
import { useCompose, usePreview, useSettled } from '@/features/dashboard/api/queries'
import { BLANK } from '@/features/dashboard/lib/formula'
import { SHOW_WORDS, nameWords, widgetUnitOf } from '@/features/dashboard/lib/time'
import type {
  ComposeChoice,
  Measure,
  Preset,
  Scope,
  TimeEvery,
  TimeShow,
  TimeSpec,
  TimeWindow,
  Vocabulary,
  WidgetDraft,
} from '@/features/mitra/api/dashboard'
import { cn } from '@/lib/utils'
import Gallery from '@/screens/dashboards/components/Gallery'
import { WidgetView } from '@/screens/dashboards/components/WidgetCards'

/**
 * Add a measure without writing a formula: Measure, For, Only and Over, each
 * a choice, and the engine writes the formula a careful analyst would
 * (`POST /v1/dashboard/compose`) -- the harmonic P/E, the size-weighted gain,
 * the per-share figure times the shares held. The result is shown worked out
 * on the reader's own rows before it is added, as a card with its picture.
 *
 * Two other ways in sit beside it: the ready-made gallery, and "Write a
 * formula" for a reader who wants the formula itself (the editor, opened
 * with whatever was composed here).
 */

const SELECT =
  'mt-1 min-h-11 w-full rounded-sm border border-border bg-background px-3 text-body-m text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'

const FOR_WORDS: Record<ComposeChoice['over'], { book: string; other: string }> = {
  book: { book: 'My holdings in total', other: 'All of them together' },
  each: { book: 'Each holding', other: 'Each company' },
  sector: { book: 'Each sector', other: 'Each sector' },
}

/** How long the builder waits after a choice before asking the engine. */
const COMPOSE_DELAY_MS = 250

/** "Now" in the When picker: today's figure, no window. */
const NOW = 'now'
/** One figure over the whole window, in the Every picker (brokers only). */
const WHOLE = 'whole'

/** The companies typed, upper-cased, once each, at most `cap`. */
function symbolsOf(text: string, cap: number): string[] {
  const named = [
    ...new Set(
      text
        .split(/[\s,]+/)
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean),
    ),
  ]
  return named.slice(0, cap)
}

/**
 * A broker widget from the builder's choices. The engine's compose knows the
 * company measures only, so a broker figure is its own name as the formula
 * (`net`), ranked largest either way and cut to ten as the engine defaults a
 * broker table, and its value is the engine's preview of exactly this.
 */
function brokerDraft(name: string, unit: string, symbols: string[], time: TimeSpec): WidgetDraft {
  const who = symbols.length ? symbols.join(', ') : 'the market'
  return {
    title: `${nameWords(name)} by broker · ${who}`,
    formula: name,
    kind: 'bars',
    unit: widgetUnitOf(unit) ?? 'ratio',
    decimals: unit === 'shares' || unit === 'count' ? 0 : 2,
    scope: { kind: 'brokers', symbols },
    time,
    rank: 'abs',
    limit: 10,
  }
}

type Tab = 'build' | 'ready'

/** A scope as the "Over" picker's value, and back. */
function scopeKeyOf(scope: Scope): string {
  if (scope.kind === 'watchlist') return `watchlist:${String(scope.id)}`
  return scope.kind
}

function scopeFrom(key: string, symbols: string): Scope {
  if (key.startsWith('watchlist:')) return { kind: 'watchlist', id: Number(key.slice(10)) }
  if (key === 'market') return { kind: 'market' }
  if (key === 'symbols') {
    const named = [
      ...new Set(
        symbols
          .split(/[\s,]+/)
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean),
      ),
    ]
    return named.length ? { kind: 'symbols', symbols: named } : { kind: 'book' }
  }
  return { kind: 'book' }
}

function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: { value: T; label: string }[]
  value: T
  onChange: (value: T) => void
}) {
  return (
    <fieldset className="flex min-w-0 flex-col gap-1">
      <legend className="text-label-m text-muted-foreground">{label}</legend>
      <div className="mt-1 flex flex-wrap gap-2">
        {options.map((option) => (
          <label
            key={option.value}
            className={cn(
              'inline-flex min-h-9 cursor-pointer items-center rounded-full border px-3 text-body-s has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ring',
              option.value === value
                ? 'border-foreground bg-foreground text-background'
                : 'border-border bg-card text-foreground hover:bg-muted',
            )}
          >
            <input
              type="radio"
              className="sr-only"
              checked={option.value === value}
              onChange={() => {
                onChange(option.value)
              }}
            />
            {option.label}
          </label>
        ))}
      </div>
    </fieldset>
  )
}

export default function MeasureBuilder({
  open,
  vocabulary,
  sections,
  saving,
  refused,
  full,
  onAdd,
  onAddPreset,
  onWriteFormula,
  onAsk,
  onClose,
}: {
  open: boolean
  vocabulary: Vocabulary
  /** The dashboard's headings, offered for where the new measure sits. */
  sections: readonly string[]
  saving: boolean
  refused: string | null
  full: boolean
  onAdd: (draft: WidgetDraft) => void
  onAddPreset: (preset: Preset, symbol?: string) => void
  onWriteFormula: (draft: WidgetDraft) => void
  onAsk: ((question: string) => void) | null
  onClose: () => void
}) {
  const id = useId()
  const [tab, setTab] = useState<Tab>('build')
  const [scopeKey, setScopeKey] = useState('book')
  const brokers = scopeKey === 'brokers'
  const time = vocabulary.time
  // WHEN: today, or a window from the engine's own list. Brokers always have one.
  const [whenKey, setWhenKey] = useState<string>(NOW)
  const windows = (time?.windows ?? []).filter((w) => w.key !== 'custom')
  const when: TimeWindow | null =
    whenKey === NOW
      ? brokers
        ? (windows[1]?.key ?? windows[0]?.key ?? null)
        : null
      : (whenKey as TimeWindow)
  const [everyKey, setEveryKey] = useState<string | null>(null)
  const [show, setShow] = useState<TimeShow>('line')
  // A company figure over a window steps (the engine fills a default); a
  // broker table is one figure per broker over the window unless stepped.
  const every: TimeEvery | null = !when
    ? null
    : everyKey === WHOLE
      ? brokers
        ? null
        : (time?.defaultEvery[when] ?? 'day')
      : ((everyKey as TimeEvery | null) ?? (brokers ? null : (time?.defaultEvery[when] ?? 'day')))
  const timeSpec: TimeSpec | null = when
    ? { window: when, from: null, to: null, every, show: every ? show : null }
    : null

  // The measures: over brokers the broker figures; over a window those with a
  // record over time (where the engine names them); otherwise every one.
  const history = new Set(vocabulary.historyVariables.map((h) => h.name))
  const measures: Measure[] = brokers
    ? vocabulary.brokerVariables
        .filter((b) => b.unit !== 'text')
        .map((b) => ({
          name: b.name,
          label: nameWords(b.name),
          unit: b.unit,
          group: 'Brokers',
          over: ['each'],
        }))
    : when && history.size > 0
      ? vocabulary.measures.filter((m) => history.has(m.name))
      : vocabulary.measures
  const [measureName, setMeasureName] = useState(vocabulary.measures[0]?.name ?? '')
  const measure: Measure | undefined = measures.find((m) => m.name === measureName) ?? measures[0]
  const [over, setOver] = useState<ComposeChoice['over']>(measure?.over[0] ?? 'book')
  const [only, setOnly] = useState('')
  const [symbols, setSymbols] = useState('')
  const [title, setTitle] = useState('')
  const [section, setSection] = useState('')

  const brokerCap = time?.limits.floorsheetSymbols ?? 3
  const brokerSymbols = brokers ? symbolsOf(symbols, brokerCap) : []
  const scope = brokers
    ? { kind: 'brokers' as const, symbols: brokerSymbols }
    : scopeFrom(scopeKey, symbols)
  const overs = measure?.over ?? []
  const shownOver = overs.includes(over) ? over : (overs[0] ?? 'book')
  const brokerKey = brokerSymbols.join(',')
  const timeKey = timeSpec
    ? `${timeSpec.window}/${String(timeSpec.every)}/${String(timeSpec.show)}`
    : ''
  const choice = useMemo<ComposeChoice | null>(
    () =>
      measure && !brokers
        ? {
            measure: measure.name,
            over: shownOver,
            ...(only.startsWith('sector:') ? { sector: only.slice(7) } : {}),
            ...(only.startsWith('symbol:') ? { symbol: only.slice(7) } : {}),
            ...(scope.kind !== 'book' ? { scope } : {}),
            ...(timeSpec ? { time: timeSpec } : {}),
          }
        : null,
    // `scope` and `timeSpec` are rebuilt every render; their keys say when they changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [measure, brokers, shownOver, only, scopeKey, symbols, timeKey],
  )
  const settled = useSettled(choice, COMPOSE_DELAY_MS)
  const composed = useCompose(open && tab === 'build' ? settled : null)
  const answer = settled === choice ? composed.data : null
  const composedOk = answer?.kind === 'ok' ? answer : null

  // Over brokers: the widget is drawn here and the engine's preview works it out.
  const brokerWidget = useMemo(
    () =>
      brokers && measure && timeSpec
        ? brokerDraft(measure.name, measure.unit, brokerSymbols, timeSpec)
        : null,
    // By the measure's name and unit: the broker measures are rebuilt from the
    // vocabulary every render, and a new object each time would never settle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brokers, measure?.name, measure?.unit, timeKey, brokerKey],
  )
  const settledBroker = useSettled(brokerWidget, COMPOSE_DELAY_MS)
  const brokerAsk = useMemo(
    () => (settledBroker ? [{ ...settledBroker, id: 'broker' }] : []),
    [settledBroker],
  )
  const brokerPreview = usePreview(brokerAsk, open && tab === 'build' && brokers)
  const brokerDrawn =
    settledBroker === brokerWidget ? (brokerPreview.data?.widgets[0] ?? null) : null
  const made = brokers
    ? brokerWidget && brokerDrawn
      ? { widget: brokerWidget, preview: brokerDrawn }
      : null
    : composedOk

  const groups = useMemo(() => [...new Set(measures.map((m) => m.group))], [measures])
  const draft = (): WidgetDraft | null =>
    made
      ? {
          ...made.widget,
          title: title.trim() || made.widget.title,
          ...(section.trim() ? { section: section.trim() } : {}),
        }
      : null
  const book = scope.kind === 'book'
  const brokersOffered =
    vocabulary.scopes.includes('brokers') && vocabulary.brokerVariables.length > 0 && time !== null

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title="Add a measure"
      size="640"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="quiet"
            data-role="write-formula"
            onClick={() => {
              onWriteFormula(draft() ?? BLANK)
            }}
          >
            Write a formula
          </Button>
          <span className="flex-1" />
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {tab === 'build' ? (
            <Button
              variant="primary"
              data-role="builder-add"
              disabled={saving || full || !made || (made.preview?.error ?? null) !== null}
              onClick={() => {
                const next = draft()
                if (next) onAdd(next)
              }}
            >
              {saving ? 'Adding…' : 'Add'}
            </Button>
          ) : null}
        </div>
      }
    >
      <div data-role="measure-builder" className="flex min-w-0 flex-col gap-4">
        <ChipTabs
          options={[
            { value: 'build', label: 'Build It' },
            { value: 'ready', label: 'Ready-Made' },
          ]}
          value={tab}
          onChange={(next) => {
            setTab(next as Tab)
          }}
          ariaLabel="How to add a measure"
        />
        {tab === 'ready' ? (
          <Gallery
            vocabulary={vocabulary}
            disabled={saving || full}
            onAdd={onAddPreset}
            onCustomise={onWriteFormula}
            onAsk={onAsk}
          />
        ) : (
          <>
            <label className="text-label-m text-muted-foreground">
              Measure
              <select
                data-role="builder-measure"
                className={SELECT}
                value={measure?.name ?? ''}
                onChange={(e) => {
                  setMeasureName(e.target.value)
                  setTitle('')
                }}
              >
                {groups.map((group) => (
                  <optgroup key={group} label={group}>
                    {measures
                      .filter((m) => m.group === group)
                      .map((m) => (
                        <option key={m.name} value={m.name}>
                          {m.label}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </select>
            </label>
            {time && windows.length > 0 ? (
              <div data-role="builder-time" className="flex flex-col gap-3">
                <Segmented
                  label="When"
                  options={[
                    ...(brokers ? [] : [{ value: NOW, label: 'Now' }]),
                    ...windows.map((w) => ({ value: w.key, label: w.label })),
                  ]}
                  value={when ?? NOW}
                  onChange={(next) => {
                    setWhenKey(next)
                    setEveryKey(null)
                    setTitle('')
                    // The whole market has no history to draw.
                    if (next !== NOW && scopeKey === 'market') setScopeKey('book')
                  }}
                />
                {when ? (
                  <Segmented
                    label="Every"
                    options={[
                      ...(brokers ? [{ value: WHOLE, label: 'Whole window' }] : []),
                      ...time.every.map((e) => ({ value: e.key, label: e.label })),
                    ]}
                    value={every ?? WHOLE}
                    onChange={(next) => {
                      setEveryKey(next)
                      setTitle('')
                    }}
                  />
                ) : null}
                {when && every ? (
                  <Segmented
                    label="Show as"
                    options={(time.show.length
                      ? time.show
                      : (['line', 'bars', 'table'] as const)
                    ).map((s) => ({ value: s, label: SHOW_WORDS[s] }))}
                    value={show}
                    onChange={setShow}
                  />
                ) : null}
              </div>
            ) : null}
            {overs.length > 1 && !brokers ? (
              <Segmented
                label="For"
                options={overs.map((o) => ({
                  value: o,
                  label: book ? FOR_WORDS[o].book : FOR_WORDS[o].other,
                }))}
                value={shownOver}
                onChange={(next) => {
                  setOver(next)
                  setTitle('')
                }}
              />
            ) : null}
            <div className="grid grid-cols-1 gap-3 @md:grid-cols-2">
              {brokers ? null : (
                <label className="text-label-m text-muted-foreground">
                  Only
                  <select
                    data-role="builder-only"
                    className={SELECT}
                    value={only}
                    onChange={(e) => {
                      setOnly(e.target.value)
                      setTitle('')
                    }}
                  >
                    <option value="">All</option>
                    {vocabulary.held.sector.length ? (
                      <optgroup label="Sector">
                        {vocabulary.held.sector.map((s) => (
                          <option key={s} value={`sector:${s}`}>
                            {s}
                          </option>
                        ))}
                      </optgroup>
                    ) : null}
                    {vocabulary.held.symbol.length ? (
                      <optgroup label="Company">
                        {vocabulary.held.symbol.map((s) => (
                          <option key={s} value={`symbol:${s}`}>
                            {s}
                          </option>
                        ))}
                      </optgroup>
                    ) : null}
                  </select>
                </label>
              )}
              <label className="text-label-m text-muted-foreground">
                Over
                <select
                  data-role="builder-scope"
                  className={SELECT}
                  value={scopeKey}
                  onChange={(e) => {
                    setScopeKey(e.target.value)
                    setTitle('')
                    if (e.target.value === 'brokers' && whenKey === NOW) {
                      setWhenKey(windows[1]?.key ?? windows[0]?.key ?? NOW)
                    }
                  }}
                >
                  <option value={scopeKeyOf({ kind: 'book' })}>My holdings</option>
                  {vocabulary.watchlists.map((w) => (
                    <option key={w.id} value={scopeKeyOf({ kind: 'watchlist', id: w.id })}>
                      {w.name}
                    </option>
                  ))}
                  <option value="symbols">Companies I name</option>
                  {/* The whole market's history is too large to draw. */}
                  {when ? null : <option value="market">The market</option>}
                  {brokersOffered ? <option value="brokers">Brokers</option> : null}
                </select>
              </label>
            </div>
            {scopeKey === 'symbols' ? (
              <Field
                label="Companies"
                value={symbols}
                placeholder="NABIL, NICA, UPPER"
                onChange={(e) => {
                  setSymbols(e.target.value)
                }}
              />
            ) : null}
            {brokers ? (
              <div className="flex flex-col">
                <Field
                  label={`Brokers in (up to ${String(brokerCap)} companies; empty for the whole market)`}
                  data-role="builder-broker-symbols"
                  value={symbols}
                  list={`${id}-held`}
                  placeholder={vocabulary.held.symbol[0] ?? 'NABIL'}
                  onChange={(e) => {
                    setSymbols(e.target.value)
                    setTitle('')
                  }}
                />
                <datalist id={`${id}-held`}>
                  {vocabulary.held.symbol.map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
              </div>
            ) : null}

            <div
              data-role="builder-preview"
              aria-live="polite"
              className="flex min-w-0 flex-col gap-2 rounded-md bg-muted p-3"
            >
              {!brokers && answer?.kind === 'refused' ? (
                <p role="alert" className="text-body-s text-destructive">
                  {answer.message}
                </p>
              ) : (!brokers && answer?.kind === 'failed') ||
                (brokers && brokerPreview.data === null && !brokerPreview.isFetching) ? (
                <p role="alert" className="text-body-s text-destructive">
                  This could not be worked out just now.
                </p>
              ) : made?.preview ? (
                <WidgetView
                  widget={{ ...made.preview, title: title.trim() || made.widget.title }}
                  scopes={[]}
                  holdings={vocabulary.held.symbol}
                />
              ) : (
                <p className="text-body-s text-muted-foreground">Working it out…</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 @md:grid-cols-2">
              <Field
                label="Title"
                value={title}
                maxLength={60}
                placeholder={made?.widget.title ?? ''}
                onChange={(e) => {
                  setTitle(e.target.value)
                }}
              />
              <div className="flex flex-col">
                <Field
                  label="Section"
                  value={section}
                  maxLength={40}
                  list={`${id}-sections`}
                  placeholder="None"
                  onChange={(e) => {
                    setSection(e.target.value)
                  }}
                />
                <datalist id={`${id}-sections`}>
                  {sections.map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
              </div>
            </div>
            {refused ? (
              <p role="alert" className="text-body-s text-destructive">
                {refused}
              </p>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  )
}
